/**
 * PPT Toolbox — sidebar shell mirroring IRLToolbox / FDDToolbox.
 *
 * One tile today (Storyboard). The shape is here so every future PPT tool —
 * template-driven generation, slide creation, branding swap — adds as a new
 * tile without rewriting the navigation.
 */

import React, { useState } from "react";
import PPTFindingsPanel from "./PPTFindingsPanel";
import CommentaryPanel from "./CommentaryPanel";

type Props = {
  projectId: string;
};

type ToolId = "storyboard" | "commentary";

const TOOLS: { id: ToolId; label: string; icon: string; description: string }[] = [
  {
    id: "storyboard",
    label: "Storyboard",
    icon: "🎯",
    description: "Generate the FDD storyboard deck from the template",
  },
  {
    id: "commentary",
    label: "Commentary",
    icon: "💬",
    description: "Extract databook tables and pick rows to comment on",
  },
];

const PPTToolbox: React.FC<Props> = ({ projectId }) => {
  const [activeTool, setActiveTool] = useState<ToolId>("storyboard");
  const tool = TOOLS.find((t) => t.id === activeTool) ?? TOOLS[0];

  return (
    <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "stretch" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: 180,
          flexShrink: 0,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "var(--theme-tint-7)",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ fontWeight: 700, color: "var(--theme-ink)", padding: "4px 6px", fontSize: 13 }}>
          PPT Tools
        </div>
        {TOOLS.map((t) => {
          const isActive = t.id === activeTool;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTool(t.id)}
              style={{
                background: isActive ? "var(--theme-accent)" : "#ffffff",
                color: isActive ? "#fff" : "var(--theme-ink)",
                border: `1px solid ${isActive ? "var(--theme-accent)" : "var(--theme-border-strong)"}`,
                borderRadius: 8,
                padding: "10px 12px",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          );
        })}
      </aside>

      {/* Main panel */}
      <main
        style={{
          flex: 1,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "#ffffff",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          <span style={{ fontWeight: 600, color: "var(--theme-ink)" }}>{tool.icon} {tool.label}</span>
          <span style={{ marginLeft: 6 }}>— {tool.description}</span>
        </div>

        {activeTool === "storyboard" && <PPTFindingsPanel projectId={projectId} />}
        {activeTool === "commentary" && <CommentaryPanel projectId={projectId} />}
      </main>
    </div>
  );
};

export default PPTToolbox;
